<template>
    <div class="preset-color-picker">
        <div class="color-choice-bar">
            <span
                v-for="color in presetColorsList"
                :key="color.hex"
                class="preset-color-block"
                :class="{ 'is-active': isActiveColor(color.hex) }"
                :style="{ backgroundColor: color.hex }"
                :title="color.label"
                @click="selectColor(color.hex)"
            />
            <el-color-picker
                v-model="pickerValue"
                size="small"
                :class="{ 'is-custom-active': isCustomColor(pickerValue) }"
                title="自定义颜色 (点击打开调色盘)"
            />
            <span v-if="showLabel" class="color-text-value" :style="{ color: displayColor }">
                {{ getColorName(displayColor) }} ({{ displayColor }})
            </span>
        </div>
    </div>
</template>

<script>
/** 与 ScreenManager 一致的快捷预设色 */
export const SCREEN_PRESET_COLORS = [
    { label: '天蓝', hex: '#3b82f6' },
    { label: '深蓝', hex: '#1d4ed8' },
    { label: '中国红', hex: '#e60012' },
    { label: '鲜红', hex: '#ef4444' },
    { label: '翡翠绿', hex: '#10b981' },
    { label: '深绿', hex: '#047857' },
    { label: '金黄', hex: '#eab308' },
    { label: '咖啡棕', hex: '#8b5a2b' },
    { label: '暗黑', hex: '#1e293b' },
];

export default {
    name: 'PresetColorPicker',
    props: {
        value: { type: String, default: '' },
        /** 自定义预设列表；默认使用 ScreenManager 同款 9 色 */
        presets: { type: Array, default: null },
        showLabel: { type: Boolean, default: true },
        fallbackColor: { type: String, default: '#3b82f6' },
    },
    computed: {
        presetColorsList() {
            return this.presets && this.presets.length ? this.presets : SCREEN_PRESET_COLORS;
        },
        displayColor() {
            return this.value || this.fallbackColor;
        },
        pickerValue: {
            get() {
                return this.value || '';
            },
            set(val) {
                this.$emit('input', val || '');
            },
        },
    },
    methods: {
        selectColor(hex) {
            this.$emit('input', hex);
        },
        isActiveColor(hex) {
            return (this.value || '').toLowerCase() === (hex || '').toLowerCase();
        },
        isCustomColor(colorHex) {
            if (!colorHex) return false;
            return !this.presetColorsList.some(
                c => c.hex.toLowerCase() === colorHex.toLowerCase()
            );
        },
        hexToRgbArray(hex) {
            if (!hex) return [0, 0, 0];
            let s = hex.replace('#', '');
            if (s.length === 3) {
                s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
            }
            const num = parseInt(s, 16);
            return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
        },
        getColorName(colorHex) {
            if (!colorHex) return '未指定';

            const colorMap = [
                { name: '天蓝色', hex: '#3b82f6', rgb: [59, 130, 246] },
                { name: '深蓝色', hex: '#1d4ed8', rgb: [29, 78, 216] },
                { name: '中国红', hex: '#e60012', rgb: [230, 0, 18] },
                { name: '鲜红色', hex: '#ef4444', rgb: [239, 68, 68] },
                { name: '翡翠绿', hex: '#10b981', rgb: [16, 185, 129] },
                { name: '深绿色', hex: '#047857', rgb: [4, 120, 87] },
                { name: '金黄色', hex: '#eab308', rgb: [234, 179, 8] },
                { name: '咖啡棕', hex: '#8b5a2b', rgb: [139, 90, 43] },
                { name: '暗黑色', hex: '#1e293b', rgb: [30, 41, 59] },
                { name: '哲商绿', hex: '#2f805f', rgb: [47, 128, 95] },
                { name: '学院蓝', hex: '#316fba', rgb: [49, 111, 186] },
                { name: '琥珀金', hex: '#b77824', rgb: [183, 120, 36] },
                { name: '紫罗兰', hex: '#865fa3', rgb: [134, 95, 163] },
                { name: '青碧色', hex: '#2e7d8a', rgb: [46, 125, 138] },
                { name: '玫瑰粉', hex: '#ec4899', rgb: [236, 72, 153] },
                { name: '薄荷绿', hex: '#14b8a6', rgb: [20, 184, 166] },
                { name: '珊瑚橙', hex: '#f97316', rgb: [249, 115, 22] },
                { name: '纯白色', hex: '#ffffff', rgb: [255, 255, 255] },
            ];

            const exactMatch = colorMap.find(c => c.hex.toLowerCase() === colorHex.toLowerCase());
            if (exactMatch) return exactMatch.name;

            const targetRgb = this.hexToRgbArray(colorHex);
            let minDistance = Infinity;
            let closestColorName = '自定义';

            colorMap.forEach(c => {
                const dist = Math.pow(targetRgb[0] - c.rgb[0], 2)
                    + Math.pow(targetRgb[1] - c.rgb[1], 2)
                    + Math.pow(targetRgb[2] - c.rgb[2], 2);
                if (dist < minDistance) {
                    minDistance = dist;
                    closestColorName = c.name;
                }
            });

            return closestColorName + '(自定义)';
        },
    },
};
</script>

<style scoped>
.color-choice-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.preset-color-block {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    box-sizing: border-box;
    display: inline-block;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    flex-shrink: 0;
}

.preset-color-block:hover {
    transform: scale(1.2) translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
}

.preset-color-block.is-active {
    border-color: #ffffff !important;
    box-shadow: 0 0 0 2.5px var(--btn-primary, #2563eb), 0 4px 10px rgba(37, 99, 235, 0.45);
    transform: scale(1.15);
}

.preset-color-picker ::v-deep .el-color-picker {
    height: 26px !important;
    line-height: 26px !important;
    vertical-align: middle;
    margin-left: 2px;
    flex-shrink: 0;
}

.preset-color-picker ::v-deep .el-color-picker__trigger {
    width: 26px !important;
    height: 26px !important;
    border-radius: 50% !important;
    border: 2px solid #ffffff !important;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15) !important;
    box-sizing: border-box;
    padding: 0 !important;
    background: transparent !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: hidden;
}

.preset-color-picker ::v-deep .el-color-picker__trigger:hover {
    transform: scale(1.2) translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25) !important;
}

.preset-color-picker ::v-deep .el-color-picker__color {
    border: none !important;
    border-radius: 50% !important;
}

.preset-color-picker ::v-deep .el-color-picker__color-inner {
    border-radius: 50% !important;
}

.preset-color-picker ::v-deep .el-color-picker__icon {
    display: none !important;
}

.preset-color-picker .is-custom-active ::v-deep .el-color-picker__trigger {
    border-color: #ffffff !important;
    box-shadow: 0 0 0 2.5px var(--btn-primary, #2563eb), 0 4px 10px rgba(37, 99, 235, 0.45) !important;
    transform: scale(1.15);
}

.preset-color-picker ::v-deep .el-color-picker__color-inner::after {
    content: '' !important;
    display: block !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    width: 10px !important;
    height: 2px !important;
    background-color: #ffffff !important;
    transform: translate(-50%, -50%) !important;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.4) !important;
    pointer-events: none !important;
    opacity: 0.9 !important;
    z-index: 10 !important;
}

.preset-color-picker ::v-deep .el-color-picker__color-inner::before {
    content: '' !important;
    display: block !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    width: 2px !important;
    height: 10px !important;
    background-color: #ffffff !important;
    transform: translate(-50%, -50%) !important;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.4) !important;
    pointer-events: none !important;
    opacity: 0.9 !important;
    z-index: 10 !important;
}

.color-text-value {
    font-family: Consolas, Monaco, monospace;
    font-size: 13px;
    font-weight: 700;
    margin-left: 4px;
    padding: 3px 8px;
    background-color: #f8fafc;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    min-width: 140px;
    white-space: nowrap;
    text-align: center;
    display: inline-block;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
    user-select: all;
    flex-shrink: 0;
}
</style>
